import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { login as apiLogin, register as apiRegister, getMe, wecomLogin as apiWecomLogin } from '@/api/auth';
import type { User } from '@/types/user';
import type { LoginRequest, RegisterRequest } from '@/types/auth';

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  loginWithWecom: (code: string, state: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // storage unavailable, proceed without persistence
  }
}

function removeToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

function storeRefreshToken(rt: string) {
  try {
    localStorage.setItem(REFRESH_KEY, rt);
  } catch {
    // storage unavailable, proceed without persistence
  }
}

function removeRefreshToken() {
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: getStoredToken(),
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // On mount, if token exists, try to fetch current user
  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    getMe()
      .then((user) => {
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      })
      .catch(() => {
        // Token invalid or expired — clear it
        removeToken();
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      });
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    setState((prev) => ({ ...prev, error: null }));
    const response = await apiLogin(data);
    storeToken(response.token);
    storeRefreshToken(response.refreshToken);
    setState({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const loginWithWecom = useCallback(async (code: string, state: string) => {
    setState((prev) => ({ ...prev, error: null }));
    const response = await apiWecomLogin(code, state);
    storeToken(response.token);
    storeRefreshToken(response.refreshToken);
    setState({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    setState((prev) => ({ ...prev, error: null }));
    const response = await apiRegister(data);
    storeToken(response.token);
    storeRefreshToken(response.refreshToken);
    setState({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  }, []);

  const logout = useCallback(() => {
    removeToken();
    removeRefreshToken();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, loginWithWecom, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}